import { useCallback, useEffect, useRef, useState } from 'react';
import { ExperimentMetric, experimentService } from 'src/services';
import { ApiError } from 'src/services/src/service/httpService-types';
import { ValidationErrorKey } from 'src/containers/ToastManager/ActionsNotifications/validation-error-key';
import { DEFAULT_MDE } from '../TrafficStep/constants';
import { RECOMMENDED_SPLIT } from '../wizard-risk-split';
import { TIER_A_METRICS, buildDefaultHypothesis, buildDefaultName } from './constants';
import { GoalStepProps, SensitivityInfo, UseGoalStepReturnType } from './types';

const SENSITIVITY_ARM_SHARE = RECOMMENDED_SPLIT.medium.variant / 100;

export const useGoalStep = ({
  shopId,
  experiment,
  baselines,
  isLoadingBaselines,
}: GoalStepProps): UseGoalStepReturnType => {
  const [selectedMetric, setSelectedMetric] = useState<ExperimentMetric | undefined>(
    experiment?.metric
  );
  const [hypothesis, setHypothesis] = useState(experiment?.hypothesis || '');
  const [name, setName] = useState(experiment?.name || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | undefined>();
  const [sensitivityByMetric, setSensitivityByMetric] = useState<
    Partial<Record<ExperimentMetric, SensitivityInfo>>
  >({});
  const [loadingSensitivityMetric, setLoadingSensitivityMetric] = useState<
    ExperimentMetric | undefined
  >();
  const fetchedMetricsRef = useRef<Set<ExperimentMetric>>(new Set());

  const selectMetric = (metric: ExperimentMetric): void => {
    setSelectedMetric(metric);

    const metricLabel = TIER_A_METRICS.find(tierAMetric => tierAMetric.metric === metric)?.label;

    if (metricLabel) {
      setHypothesis(buildDefaultHypothesis(metricLabel));
    }

    if (!name) {
      setName(buildDefaultName());
    }
  };

  useEffect(() => {
    if (isLoadingBaselines || selectedMetric) return;

    const defaultMetric =
      TIER_A_METRICS.find(({ metric }) => baselines.find(b => b.metric === metric)?.hasBaseline)
        ?.metric || TIER_A_METRICS[0]?.metric;

    if (defaultMetric) {
      selectMetric(defaultMetric);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingBaselines, baselines, selectedMetric]);

  useEffect(() => {
    if (!selectedMetric || fetchedMetricsRef.current.has(selectedMetric)) return undefined;

    setLoadingSensitivityMetric(selectedMetric);

    let cancelled = false;

    experimentService
      .getWizardEstimate({
        shopId,
        metric: selectedMetric,
        mde: DEFAULT_MDE,
        armShare: SENSITIVITY_ARM_SHARE,
      })
      .then(result => {
        if (cancelled) return;
        // Only mark as fetched once a result is actually in hand - marking it eagerly before
        // the request settles means a cancelled in-flight request (e.g. the user switches to
        // this metric, away, then back before the first request resolves) would permanently
        // block any retry, since the cleanup below never resolves/rejects to undo it.
        fetchedMetricsRef.current.add(selectedMetric);
        setSensitivityByMetric(prev => ({
          ...prev,
          [selectedMetric]:
            result.status === 'ok'
              ? {
                  status: 'ok',
                  dailyTestUsers: result.dailyTestUsers,
                  minEffectPercent: DEFAULT_MDE,
                }
              : { status: 'unavailable', reason: result.reason },
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setSensitivityByMetric(prev => ({
          ...prev,
          [selectedMetric]: { status: 'unavailable', reason: 'Failed to load estimate' },
        }));
      })
      .finally(() => {
        if (!cancelled) setLoadingSensitivityMetric(undefined);
      });

    return () => {
      cancelled = true;
    };
  }, [shopId, selectedMetric]);

  const onSubmit = useCallback((): Promise<boolean> => {
    if (!experiment || !selectedMetric) return Promise.resolve(false);

    setNameError(undefined);
    setIsSubmitting(true);
    return experimentService
      .updateExperiment({
        shopId,
        experiment: {
          id: experiment.id,
          metric: selectedMetric,
          hypothesis,
          name,
        },
      })
      .then(() => true)
      .catch((error: ApiError) => {
        if (error.errorKey === ValidationErrorKey.ExperimentNameAlreadyExists) {
          setNameError('This name is already taken. Please choose another one.');
        }
        return false;
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }, [experiment, selectedMetric, shopId, hypothesis, name]);

  const setNameAndClearError = useCallback((nextName: string) => {
    setNameError(undefined);
    setName(nextName);
  }, []);

  return {
    baselines,
    isLoadingBaselines,
    selectedMetric,
    hypothesis,
    name,
    nameError,
    selectMetric,
    setHypothesis,
    setName: setNameAndClearError,
    onSubmit,
    isSubmitting,
    canContinue: !!selectedMetric && name.trim().length >= 3,
    sensitivity: selectedMetric ? sensitivityByMetric[selectedMetric] : undefined,
    isLoadingSensitivity: !!selectedMetric && loadingSensitivityMetric === selectedMetric,
  };
};
